// In-memory hand-offs between the sign-in screens (WEB_PLAN.md §4.2, §5.1). Deliberately NOT the URL and NOT
// storage: the password typed at sign-up has to be saved again right after the emailed code confirms the account
// (the server trigger `groundteam_discard_unconfirmed_password` drops any password set before confirmation), so it
// lives here for the few minutes between "Create account" and "Verify", and is cleared the moment it is used. A page
// reload loses it on purpose (the verify screen then sends the person back to sign in).
//
// Also the "finishing sign-in" gate: while a verified code's password is being saved, the new Supabase session must
// not count as signed in yet (iOS `SyncCenter.isFinishingSignIn`). AuthProvider ignores sessions while the gate is
// held and resolves the account once it is released; the signed-out routes stay mounted meanwhile.

/** A sign-up (or sign-in to a never-confirmed account) waiting for its emailed code. */
export interface PendingVerification {
  /** Trimmed, lower-cased address the code was sent to. */
  email: string;
  /** Where "Wrong email? Go back" returns to. */
  origin: 'signUp' | 'signIn';
  /** When the code was (last) sent, for the 30 s resend timer. */
  sentAt: number;
}

let verification: PendingVerification | null = null;
let pendingPassword: { email: string; password: string } | null = null;
let rememberedEmail = '';

/** Remember the address and password until the code is entered (`takePendingPassword`). */
export function startVerification(email: string, password: string, origin: PendingVerification['origin']): void {
  verification = { email, origin, sentAt: Date.now() };
  pendingPassword = { email, password };
  rememberedEmail = email;
}

export function currentVerification(): PendingVerification | null {
  return verification;
}

/** "Resend code" succeeded: restart the timer. */
export function markCodeResent(): void {
  if (verification) verification = { ...verification, sentAt: Date.now() };
}

/** The password typed for `email` (once; it is forgotten on read). Null when it belongs to another address. */
export function takePendingPassword(email: string): string | null {
  if (!pendingPassword || pendingPassword.email !== email) return null;
  const { password } = pendingPassword;
  pendingPassword = null;
  return password;
}

/** Leaving the verify step (go back, finished, signed out): forget the address and the password. */
export function clearVerification(): void {
  verification = null;
  pendingPassword = null;
}

/** The address last typed on a sign-in screen, to prefill the next one (Forgot password, Go back). */
export function rememberEmail(email: string): void {
  rememberedEmail = email.trim();
}

export function recalledEmail(): string {
  return rememberedEmail;
}

// ---------------------------------------------------------------------------------------------------------------------
// Finishing gate
// ---------------------------------------------------------------------------------------------------------------------

let finishing = 0;
const gateListeners = new Set<() => void>();

/** A verified code's session is on its way: don't treat it as signed in until `endFinishingSignIn`. */
export function beginFinishingSignIn(): void {
  finishing += 1;
}

/** The password step is over (saved, failed and signed out, or the notice was acknowledged). */
export function endFinishingSignIn(): void {
  if (finishing === 0) return;
  finishing -= 1;
  if (finishing === 0) for (const fn of [...gateListeners]) fn();
}

export function isFinishingSignIn(): boolean {
  return finishing > 0;
}

/** Called when the gate is released (AuthProvider then resolves the session). Returns an unsubscribe. */
export function onFinishingReleased(fn: () => void): () => void {
  gateListeners.add(fn);
  return () => gateListeners.delete(fn);
}

/**
 * The one hold a flow may keep after it returns: a verified sign-up whose password couldn't be saved keeps the
 * session uncounted so the "password wasn't saved" notice stays on screen. Kept as a flag, not a count, so releasing
 * it is idempotent (Continue, leaving the page and signing out may all try) and a second kept hold replaces the
 * first instead of stacking up: a leaked hold would make every later sign-in in this tab look ignored.
 */
let noticeHold = false;

/** Called INSTEAD of `endFinishingSignIn` by a flow that keeps its hold for the notice. */
export function keepFinishingForNotice(): void {
  if (noticeHold) endFinishingSignIn(); // the earlier kept hold goes; this flow's hold takes its place
  noticeHold = true;
}

/** Releases the kept hold, if any (the session then counts as signed in). */
export function releaseNoticeHold(): void {
  if (!noticeHold) return;
  noticeHold = false;
  endFinishingSignIn();
}

// ---------------------------------------------------------------------------------------------------------------------
// One-off notices across a sign-out
// ---------------------------------------------------------------------------------------------------------------------

export interface AuthNotice {
  title: string;
  message: string;
}

let signedOutNotice: AuthNotice | null = null;

/** Shown once on the sign-in screen (e.g. "Account deleted", "Please sign in again"). */
export function setSignedOutNotice(notice: AuthNotice | null): void {
  signedOutNotice = notice;
}

export function takeSignedOutNotice(): AuthNotice | null {
  const n = signedOutNotice;
  signedOutNotice = null;
  return n;
}

/** The pending notice without consuming it (for render; consume with `takeSignedOutNotice` in an effect). */
export function peekSignedOutNotice(): AuthNotice | null {
  return signedOutNotice;
}

// ---------------------------------------------------------------------------------------------------------------------
// Name shared by Sign in with Apple
// ---------------------------------------------------------------------------------------------------------------------

export interface SharedProfileName {
  first: string;
  last: string;
  /** The Supabase user the name belongs to (a different account signing in must not get it). */
  userId: string;
}

let sharedName: SharedProfileName | null = null;

/**
 * Apple shares the person's name only the first time they consent (iOS keeps it as `pendingProfileName` for the
 * admin profile of a new company). The welcome / create-company screen reads it with `sharedProfileName(userId)`.
 */
export function setSharedProfileName(name: SharedProfileName | null): void {
  sharedName = name && (name.first.trim() || name.last.trim()) ? name : null;
}

export function sharedProfileName(userId: string): { first: string; last: string } | null {
  if (!sharedName || sharedName.userId !== userId) return null;
  return { first: sharedName.first, last: sharedName.last };
}

/** Everything above, on sign-out (including a hold kept for the "password wasn't saved" notice). */
export function forgetPendingAuth(): void {
  clearVerification();
  sharedName = null;
  releaseNoticeHold();
}
